import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InventorySkuRow, ReceiveDeliveryInput, SkuCategory } from '@gutterinv/shared';
import { postReceiveDelivery } from '../api/client.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { SkuSearchSelect } from './SkuSearchSelect.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.js';
import { formatCents } from '../lib/format.js';
import { qk } from '../lib/queryKeys.js';

export function ReceiveDeliveryDialog({
  open,
  onOpenChange,
  skus,
  categories,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skus: InventorySkuRow[];
  /** All categories for the picker's category → attribute cascade filter. */
  categories: SkuCategory[];
}) {
  const queryClient = useQueryClient();
  const [skuId, setSkuId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCostDollars, setUnitCostDollars] = useState('');
  const [notes, setNotes] = useState('');

  // Reset the form whenever the modal opens.
  useEffect(() => {
    if (open) {
      setSkuId('');
      setQuantity('');
      setUnitCostDollars('');
      setNotes('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: postReceiveDelivery,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
      onOpenChange(false);
    },
  });

  const selectedSku = skus.find((s) => s.skuId === skuId) ?? null;

  const handleSave = () => {
    const qty = Number(quantity);
    if (!skuId || Number.isNaN(qty) || qty <= 0) return;
    const dollars = unitCostDollars.trim();
    const cents = dollars === '' ? null : Math.round(Number(dollars) * 100);
    if (cents !== null && (Number.isNaN(cents) || cents < 0)) return;
    const payload: ReceiveDeliveryInput = {
      skuId,
      quantity: qty,
      unitCostCents: cents,
      notes: notes.trim() || null,
    };
    mutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Fixed, large dialog (owner 2026-10-07): the SKU picker panel sizes
          itself against the dialog, so a tall + wide dialog gives the search
          list and the cascade filters room. Fixed dimensions, viewport-capped;
          the body row takes the slack so the footer stays pinned. */}
      <DialogContent className="h-[871px] max-h-[90vh] w-[1162px] max-w-[96vw] grid-rows-[auto_1fr_auto] sm:max-w-[1162px]">
        <DialogHeader>
          <DialogTitle>Receive delivery</DialogTitle>
          <DialogDescription>
            Record qty + (optional) unit cost. Updates on-hand and the cost basis.
          </DialogDescription>
        </DialogHeader>

        {/* data-sku-panel-bound: the open SKU panel sizes itself to THIS body row
            (the 1fr grid row), so it stops above the Cancel / Receive footer. */}
        <div className="space-y-4 py-2" data-sku-panel-bound>
          <div>
            <label htmlFor="receive-sku" className="eyebrow mb-1.5 block">
              SKU *
            </label>
            {/* Same searchable picker as Jobs › Add SKU (type-ahead + category →
                attribute cascade), in its LEFT-RAIL layout (owner pick j1 = B,
                _parts-picker/2026-10-07-receive-sections/picks.json). Receiving is
                not job-scoped: productLine null → no trade restriction. */}
            <SkuSearchSelect
              id="receive-sku"
              value={skuId}
              skus={skus}
              categories={categories}
              productLine={null}
              onChange={setSkuId}
              disabled={mutation.isPending}
              placeholder="Search SKU…"
              layout="rail"
            />
            {selectedSku && (
              <p className="mt-1 text-xs text-taupe">
                Currently {selectedSku.onHand.toLocaleString()} {selectedSku.unit} on hand
                {selectedSku.costCents !== null && (
                  <>
                    {' '}· last cost {formatCents(selectedSku.costCents)}/{selectedSku.unit}
                  </>
                )}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="receive-qty" className="eyebrow mb-1.5 block">
                Quantity *
              </label>
              <Input
                id="receive-qty"
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={mutation.isPending}
                placeholder={selectedSku ? `e.g. 100 ${selectedSku.unit}` : 'How many'}
              />
            </div>
            <div>
              <label htmlFor="receive-cost" className="eyebrow mb-1.5 block">
                Unit cost ($)
              </label>
              <Input
                id="receive-cost"
                type="number"
                min="0"
                step="0.01"
                value={unitCostDollars}
                onChange={(e) => setUnitCostDollars(e.target.value)}
                disabled={mutation.isPending}
                placeholder="Optional"
              />
              <p className="mt-1 text-xs text-taupe">
                If set, updates SKU cost basis
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="receive-notes" className="eyebrow mb-1.5 block">
              Notes
            </label>
            <textarea
              id="receive-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Vendor, PO #, anything worth remembering"
              rows={2}
              disabled={mutation.isPending}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {mutation.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending || !skuId || !quantity}
          >
            {mutation.isPending ? 'Saving…' : 'Receive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
